import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { teamMembers } from '../data/mockData';
import { X } from 'lucide-react';

const TeamSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [selectedMember, setSelectedMember] = useState(null);

  const getBio = (member) => {
    if (language === 'ar') return member.bioAr;
    if (language === 'ku') return member.bioKu;
    return member.bioEn;
  };

  return (
    <section id="team" className="py-20 bg-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #c4a35a 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1d2e] mb-4">
            {t.teamTitle}
          </h2>
          <div className="w-20 h-1 bg-[#c4a35a] mx-auto mb-6" />
          <p className="text-gray-600 text-lg">
            {t.teamSubtitle}
          </p>
        </div>

        {/* Team Triangle Layout */}
        <div className="max-w-4xl mx-auto">
          {/* Top - Large Circle */}
          <div className="flex justify-center mb-8">
            {teamMembers.filter(m => m.size === 'large').map((member) => (
              <div key={member.id} className="group text-center">
                <div className="relative mb-6">
                  <div 
                    className="relative w-64 h-64 mx-auto cursor-pointer"
                    onClick={() => setSelectedMember(selectedMember?.id === member.id ? null : member)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#c4a35a] to-[#c4a35a]/50 rounded-full" />
                    <div className="absolute inset-1 bg-white rounded-full" />
                    <div className="absolute inset-2 rounded-full overflow-hidden bg-gray-100">
                      {member.image && (
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                      )}
                    </div>
                    <div className="absolute inset-2 bg-[#c4a35a]/0 group-hover:bg-[#c4a35a]/20 rounded-full transition-all duration-300" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-[#1a1d2e] mb-2 group-hover:text-[#c4a35a] transition-colors">
                  {language === 'ar' ? member.nameAr : language === 'ku' ? member.nameKu : member.name}
                </h3>
                <p className="text-[#c4a35a] text-base font-medium">
                  {t[member.position]}
                </p>
              </div>
            ))}
          </div>

          {/* Bio Display for Large Circle */}
          {selectedMember && selectedMember.size === 'large' && (
            <div className="mb-12 animate-fadeIn">
              <div className="bg-gradient-to-br from-[#1a1d2e] to-[#2a2d3e] rounded-2xl p-8 shadow-2xl relative border-2 border-[#c4a35a]/30">
                <button
                  onClick={() => setSelectedMember(null)}
                  className="absolute top-4 right-4 text-[#c4a35a] hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
                <div className="text-white whitespace-pre-line text-base leading-relaxed" style={{ direction: language === 'ar' || language === 'ku' ? 'rtl' : 'ltr' }}>
                  {getBio(selectedMember)}
                </div>
              </div>
            </div>
          )}

          {/* Bottom - Two Medium Circles */}
          <div className="grid grid-cols-2 gap-8 max-w-3xl mx-auto">
            {teamMembers.filter(m => m.size === 'medium').map((member) => (
              <div key={member.id} className="group text-center">
                <div className="relative mb-6">
                  <div 
                    className="relative w-48 h-48 mx-auto cursor-pointer"
                    onClick={() => setSelectedMember(selectedMember?.id === member.id ? null : member)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#c4a35a] to-[#c4a35a]/50 rounded-full" />
                    <div className="absolute inset-1 bg-white rounded-full" />
                    <div className="absolute inset-2 rounded-full overflow-hidden bg-gray-100">
                      {member.image && (
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      )}
                    </div>
                    <div className="absolute inset-2 bg-[#c4a35a]/0 group-hover:bg-[#c4a35a]/20 rounded-full transition-all duration-300" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-[#1a1d2e] mb-2 group-hover:text-[#c4a35a] transition-colors">
                  {language === 'ar' ? member.nameAr : language === 'ku' ? member.nameKu : member.name}
                </h3>
                <p className="text-[#c4a35a] text-sm font-medium">
                  {t[member.position]}
                </p>
              </div>
            ))}
          </div>

          {/* Bio Display for Medium Circles */}
          {selectedMember && selectedMember.size === 'medium' && (
            <div className="mt-12 animate-fadeIn">
              <div className="bg-gradient-to-br from-[#1a1d2e] to-[#2a2d3e] rounded-2xl p-8 shadow-2xl relative border-2 border-[#c4a35a]/30">
                <button
                  onClick={() => setSelectedMember(null)}
                  className="absolute top-4 right-4 text-[#c4a35a] hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
                <div className="text-white whitespace-pre-line text-base leading-relaxed" style={{ direction: language === 'ar' || language === 'ku' ? 'rtl' : 'ltr' }}>
                  {getBio(selectedMember)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
